import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import apiClient from "../utils/axiosConfig";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

// The five activity windows shown on the analytics dashboard (minutes) and how often each refreshes.
const INTERVALS = [2, 60, 720, 1440, 10080];
const REFRESH_MS = {
  2: 60000,           // every minute
  60: 3600000,        // every hour
  720: 43200000,      // every 12 hours
  1440: 86400000,     // every day
  10080: 604800000,   // every week
};

export const GatewayDataContext = createContext();

export const GatewayDataProvider = ({children}) => {
    const [data, setData] = useState({
        currentlyOnline: 0,
        pastHour: 0,
        pastTwelveHours: 0,
        pastDay: 0,
        pastWeek: 0,
        totalGateway: 0,
    });

    const [isAuthenticated, setIsAuthenticated] = useState(
      () => localStorage.getItem("isAuthenticated") === "true"
    );

    // Number of mounted analytics views using the stats. Fetching/polling only runs while > 0,
    // so users who never open analytics (e.g. customers) don't generate these requests.
    const [subscriberCount, setSubscriberCount] = useState(0);
    const isActive = isAuthenticated && subscriberCount > 0;

    const fetchData = useCallback(async (interval) => {
        try{
            const response = await apiClient.get(`${apiBaseUrl}/analytics/getactivegateway?interval=${interval}`);

            if(interval === 2){
                setData((prevState) => ({
                    ...prevState,
                    currentlyOnline: response.data.activeGateway,
                    totalGateway: response.data.totalGateway,
                }))
            } else if (interval === 60){
                setData((prevState) => ({
                    ...prevState,
                    pastHour: response.data.activeGatewayPastHour,
                    totalGateway: response.data.totalGateway,
                }))
            } else if (interval === 720){
                setData((prevState) => ({
                    ...prevState,
                    pastTwelveHours: response.data.activeGatewayPastTwelveHours,
                    totalGateway: response.data.totalGateway,
                }))
            } else if (interval === 1440){
                setData((prevState) => ({
                    ...prevState,
                    pastDay: response.data.activeGatewayPastDay,
                    totalGateway: response.data.totalGateway,
                }))
            } else if (interval === 10080){
                setData((prevState) => ({
                    ...prevState,
                    pastWeek: response.data.activeGatewayPastWeek,
                    totalGateway: response.data.totalGateway,
                }))
            }
        } catch (error) {
            console.error(`Error fetching data : ${error}`);
        }
    }, []);

    /** Register a consumer; returns the unsubscribe function (use via useActiveGatewayStats). */
    const subscribe = useCallback(() => {
        setSubscriberCount((count) => count + 1);
        return () => setSubscriberCount((count) => Math.max(0, count - 1));
    }, []);

    useEffect(() => {
      if (!isActive) return undefined;

      // Initial load: the five calls are independent, fire them in parallel.
      INTERVALS.forEach((interval) => fetchData(interval));

      // Slower windows keep their own timers.
      const slowTimers = INTERVALS.filter((i) => i !== 2).map((interval) =>
        setInterval(() => fetchData(interval), REFRESH_MS[interval])
      );

      // The 1-minute "currently online" poll pauses while the tab is hidden.
      let fastTimer = null;
      const startFast = () => {
        if (fastTimer === null) fastTimer = setInterval(() => fetchData(2), REFRESH_MS[2]);
      };
      const stopFast = () => {
        if (fastTimer !== null) {
          clearInterval(fastTimer);
          fastTimer = null;
        }
      };
      const handleVisibility = () => {
        if (document.hidden) {
          stopFast();
        } else {
          fetchData(2);
          startFast();
        }
      };

      if (!document.hidden) startFast();
      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
        document.removeEventListener("visibilitychange", handleVisibility);
        stopFast();
        slowTimers.forEach(clearInterval);
      };
    }, [isActive, fetchData]);

      const authenticate = (authStatus) => {
        setIsAuthenticated(authStatus);
        localStorage.setItem("isAuthenticated", authStatus); // Persist the auth status in localStorage
      };
      return (
        <GatewayDataContext.Provider value={{ data, fetchData, setIsAuthenticated : authenticate, subscribe }}>
          {children}
        </GatewayDataContext.Provider>
      );
}

/**
 * Use the active-gateway stats in an analytics view. Starts fetching/polling while at least
 * one component using this hook is mounted; stops when the last one unmounts.
 */
export function useActiveGatewayStats() {
  const { data, fetchData, subscribe } = useContext(GatewayDataContext);
  useEffect(() => subscribe(), [subscribe]);
  return { data, fetchData };
}
